
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DeliveryHistory.css';
import {
    Package, MapPin, Phone,
    CheckCircle, XCircle, ArrowLeft, Eye,
    Navigation, AlertCircle, Loader, MapPinOff, QrCode, Smartphone
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import QRCode from 'react-qr-code';

const API_BASE_URL = 'https://api.rismicmart.com';

const DeliveryHistory = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Tab state
    const [activeTab, setActiveTab] = useState('myOrders');

    // Orders state
    const [activeOrders, setActiveOrders] = useState([]);
    const [deliveredOrders, setDeliveredOrders] = useState([]);
    const [cancelledOrders, setCancelledOrders] = useState([]);
    const [fetchingOrders, setFetchingOrders] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderDetail, setShowOrderDetail] = useState(false);

    // UPI Payment Scanner State
    const [showPaymentScanner, setShowPaymentScanner] = useState(false);
    const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
    const [paymentVerified, setPaymentVerified] = useState(false);

    // OTP Verification State
    const [showOTPModal, setShowOTPModal] = useState(false);
    const [selectedOrderForOTP, setSelectedOrderForOTP] = useState(null);
    const [deliveryOTP, setDeliveryOTP] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(5);

    // Status from localStorage
    const [isOnline, setIsOnline] = useState(() => {
        const savedStatus = localStorage.getItem('userStatus');
        if (!savedStatus) {
            return false;
        }
        return savedStatus === 'Available';
    });

    // Location state
    const [currentLocation, setCurrentLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);
    const [watchId, setWatchId] = useState(null);
    const [locationEnabled, setLocationEnabled] = useState(() => {
        const savedGPSStatus = localStorage.getItem('gpsEnabled');
        return savedGPSStatus === 'true';
    });

    const [popupMessage, setPopupMessage] = useState("");
    const [popupData, setPopupData] = useState(null);

    // Socket.IO State
    const socketRef = useRef(null);
    const [socketConnected, setSocketConnected] = useState(false);

    // Axios instance
    const axiosInstance = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            'Content-Type': 'application/json',
        }
    });

    axiosInstance.interceptors.request.use((config) => {
        const token = localStorage.getItem('deliveryToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    // ========== UPI PAYMENT FUNCTIONS ==========
    const openPaymentScanner = (order) => {
        if (order.paymentMethod !== 'Cash on Delivery') {
            alert('This order is not Cash on Delivery. No online payment option available.');
            return;
        }

        setSelectedOrderForPayment(order);
        setShowPaymentScanner(true);
        setPaymentVerified(false);
    };

    const closePaymentScanner = () => {
        setShowPaymentScanner(false);
        setSelectedOrderForPayment(null);
        setPaymentVerified(false);
    };

    const confirmPaymentReceived = async () => {
        if (!selectedOrderForPayment) return;

        setPopupData({
            type: "confirm",
            title: "Verify UPI Payment",
            message: "Have you verified the payment in your UPI app before marking as delivered?",
            onConfirm: async () => {
                setPopupData(null);

                try {
                    const response = await axiosInstance.put(`/api/orders/${selectedOrderForPayment._id}`, {
                        status: "delivered",
                        deliveryPersonId: user._id,
                        deliveredAt: new Date(),
                        paymentMethod: "UPI Online",
                        upiVerified: true,
                        upiVerifiedAt: new Date(),
                    });

                    if (response.data) {
                        if (socketRef.current?.connected) {
                            socketRef.current.emit("order:status-update", {
                                orderId: selectedOrderForPayment._id,
                                status: "delivered",
                                deliveryPersonId: user._id,
                                paymentMethod: "UPI Online",
                            });
                        }

                        setPopupData({
                            type: "success",
                            title: "Payment Confirmed",
                            message: "Order delivered successfully with UPI payment!",
                            onConfirm: () => {
                                setPopupData(null);
                                closePaymentScanner();
                                fetchAllOrders();
                            },
                        });

                        setTimeout(() => {
                            setPopupData((prev) => {
                                if (prev && prev.type === "success" && typeof prev.onConfirm === "function") {
                                    prev.onConfirm();
                                }
                                return null;
                            });
                        }, 1500);
                    }
                } catch (error) {
                    console.error("Error confirming payment:", error);
                    setPopupData({
                        type: "error",
                        title: "Payment Failed",
                        message: "Could not confirm payment. Please try again.",
                        onConfirm: () => setPopupData(null),
                    });

                    setTimeout(() => setPopupData(null), 2500);
                }
            },
        });
    };

    // Generate UPI payment string
    const generateUPIString = (order) => {
        const upiId = 'rismicmartpriva145433.rzp@rxairtel';
        const payeeName = 'Rismicmart Private Limited';
        const amount = parseFloat(order.amount).toFixed(2);
        const orderId = order._id.slice(-6).toUpperCase();

        return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(`Order ${orderId}`)}`;
    };

    // ========== OTP VERIFICATION FUNCTIONS ==========
    const openOTPVerificationModal = (order) => {
        setSelectedOrderForOTP(order);
        setShowOTPModal(true);
        setDeliveryOTP('');
        setOtpError('');
        setOtpAttemptsLeft(5);
    };

    const handleVerifyDeliveryOTP = async () => {
        if (!deliveryOTP || deliveryOTP.length !== 6) {
            setOtpError('Please enter a valid 6-digit OTP');
            return;
        }

        setOtpLoading(true);
        setOtpError('');

        try {
            const response = await axiosInstance.put(
                `/api/delivery-person/${user._id}/orders/${selectedOrderForOTP._id}/verify-otp`,
                { otp: deliveryOTP }
            );

            if (response.data.success) {
                if (socketRef.current?.connected) {
                    socketRef.current.emit('order:status-update', {
                        orderId: selectedOrderForOTP._id,
                        status: 'out_for_delivery',
                        deliveryPersonId: user._id
                    });
                }

                setShowOTPModal(false);
                setPopupData({
                    type: "success",
                    title: "OTP Verified",
                    message: "Order is now out for delivery!",
                    onConfirm: () => {
                        setPopupData(null);
                        fetchAllOrders();
                    }
                });

                setTimeout(() => {
                    setPopupData(null);
                    fetchAllOrders();
                }, 1800);
            }
        } catch (error) {
            console.error('❌ Error verifying OTP:', error);

            const errorMessage = error.response?.data?.message || 'Failed to verify OTP';
            const attemptsLeft = error.response?.data?.attemptsLeft;

            setOtpError(errorMessage);
            if (attemptsLeft !== undefined) {
                setOtpAttemptsLeft(attemptsLeft);
            }
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendDeliveryOTP = async () => {
        setOtpLoading(true);
        setOtpError('');

        try {
            const response = await axiosInstance.post(
                `/api/delivery-person/${user._id}/orders/${selectedOrderForOTP._id}/resend-delivery-otp`
            );

            if (response.data.success) {
                setPopupData({
                    type: "success",
                    title: "OTP Resent",
                    message: "New OTP sent to customer's email!",
                    onConfirm: () => setPopupData(null)
                });

                setTimeout(() => setPopupData(null), 2000);
                setOtpAttemptsLeft(5);
            }
        } catch (error) {
            console.error('❌ Error resending OTP:', error);
            setOtpError('Failed to resend OTP. Please try again.');
        } finally {
            setOtpLoading(false);
        }
    };

    // ========== STATUS AND LOCATION SYNC ==========
    useEffect(() => {
        const savedStatus = localStorage.getItem('userStatus');
        if (savedStatus) {
            setIsOnline(savedStatus === 'Available');
        }
    }, []);

    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'userStatus') {
                setIsOnline(e.newValue === 'Available');
                console.log('✅ Status updated from localStorage:', e.newValue);
            }
            if (e.key === 'gpsEnabled') {
                const newGPSState = e.newValue === 'true';
                console.log('✅ GPS state updated from localStorage:', e.newValue);
                if (newGPSState && !locationEnabled) {
                    setLocationEnabled(true);
                    startLocationTracking();
                } else if (!newGPSState && locationEnabled) {
                    stopLocationTracking();
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);

        const interval = setInterval(() => {
            const savedStatus = localStorage.getItem('userStatus');
            if (savedStatus) {
                const newOnlineState = savedStatus === 'Available';
                setIsOnline(prevOnline => {
                    if (prevOnline !== newOnlineState) {
                        console.log('✅ Status synced from localStorage:', savedStatus);
                    }
                    return newOnlineState;
                });
            }

            const savedGPSStatus = localStorage.getItem('gpsEnabled');
            if (savedGPSStatus) {
                const newGPSState = savedGPSStatus === 'true';

                if (newGPSState && !locationEnabled && !locationLoading) {
                    console.log('🔄 GPS synced from localStorage: Starting tracking...');
                    setLocationEnabled(true);
                    startLocationTracking();
                } else if (!newGPSState && locationEnabled) {
                    console.log('🔄 GPS synced from localStorage: Stopping tracking...');
                    stopLocationTracking();
                }
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [locationEnabled, locationLoading]);

    useEffect(() => {
        const savedGPSStatus = localStorage.getItem('gpsEnabled');
        if (savedGPSStatus === 'true' && !currentLocation && !locationLoading) {
            console.log('🔄 Auto-starting GPS from saved state...');
            startLocationTracking();
        }
    }, []);

    // ========== FETCH ALL ORDERS (CORRECTED - REMOVED 'processing') ==========
    const fetchAllOrders = useCallback(async () => {
        if (!user?._id) {
            setActiveOrders([]);
            setDeliveredOrders([]);
            setCancelledOrders([]);
            setFetchingOrders(false);
            return;
        }

        try {
            setFetchingOrders(true);
            const response = await axiosInstance.get(
                `/api/orders/delivery-person/${user._id}`
            );

            if (response.data.success) {
                const allOrders = response.data.data || [];

                // CORRECTED: Removed 'processing' from active orders filter
                const active = isOnline ? allOrders.filter(order =>
                    ['confirmed', 'shipped', 'out_for_delivery'].includes(order.status)
                ) : [];

                const delivered = allOrders.filter(order =>
                    order.status === 'delivered'
                );

                const cancelled = allOrders.filter(order =>
                    order.status === 'cancelled'
                );

                const sortedActive = active.sort((a, b) =>
                    new Date(b.updatedAt || b.date) - new Date(a.updatedAt || a.date)
                );

                const sortedDelivered = delivered.sort((a, b) =>
                    new Date(b.deliveredAt || b.updatedAt) - new Date(a.deliveredAt || a.updatedAt)
                );

                const sortedCancelled = cancelled.sort((a, b) =>
                    new Date(b.cancelledAt || b.updatedAt) - new Date(a.cancelledAt || a.updatedAt)
                );

                setActiveOrders(sortedActive);
                setDeliveredOrders(sortedDelivered);
                setCancelledOrders(sortedCancelled);
                console.log('✅ Fetched orders - Active:', sortedActive.length, 'Delivered:', sortedDelivered.length, 'Cancelled:', sortedCancelled.length);
            }
        } catch (error) {
            console.error('❌ Error fetching orders:', error);
            if (error.response?.status === 401) {
                logout();
                navigate('/login');
            }
        } finally {
            setFetchingOrders(false);
        }
    }, [user?._id, isOnline, navigate, logout]);

    // ========== EMIT LOCATION TO SOCKET.IO ==========
    const emitLocationUpdate = useCallback((location) => {
        if (!socketRef.current?.connected || !location) return;

        activeOrders.forEach(order => {
            const locationData = {
                orderId: order._id,
                deliveryPersonId: user._id,
                location: {
                    latitude: location.lat,
                    longitude: location.lng,
                    accuracy: location.accuracy || 10
                },
                timestamp: new Date().toISOString(),
                deliveryPerson: {
                    fullName: user.fullName,
                    phone: user.phone,
                    vehicleType: user.vehicleType,
                    vehicleNumber: user.vehicleNumber,
                    profileImage: user.profileImage,
                    ratings: user.ratings
                }
            };

            console.log('📍 Emitting location update via Socket.IO:', locationData);
            socketRef.current.emit('delivery:location-update', locationData);
        });

        updateLocationInDatabase(location);
    }, [activeOrders, user]);

    const updateLocationInDatabase = async (location) => {
        try {
            await axiosInstance.put(`/api/delivery-person/${user._id}/location`, {
                latitude: location.lat,
                longitude: location.lng,
                accuracy: location.accuracy || 10,
                timestamp: new Date()
            });
            console.log('✅ Location saved to database');
        } catch (error) {
            console.error('❌ Failed to save location to database:', error);
        }
    };

    // ========== SOCKET.IO CONNECTION ==========
    const connectSocket = useCallback(() => {
        if (socketRef.current?.connected) {
            console.log('✅ Socket already connected');
            return;
        }

        console.log('🔌 Connecting delivery person to Socket.IO...');
        socketRef.current = io(API_BASE_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5,
            auth: {
                deliveryPersonId: user?._id,
                role: 'delivery'
            }
        });

        socketRef.current.on('connect', () => {
            console.log('✅ Delivery Socket.IO connected:', socketRef.current.id);
            setSocketConnected(true);

            activeOrders.forEach(order => {
                socketRef.current.emit('delivery:register', {
                    orderId: order._id,
                    deliveryPersonId: user._id
                });
            });
        });

        socketRef.current.on('connect_error', (error) => {
            console.error('❌ Socket connection error:', error);
            setSocketConnected(false);
        });

        socketRef.current.on('disconnect', (reason) => {
            console.log('🔌 Socket disconnected:', reason);
            setSocketConnected(false);
        });

        socketRef.current.on('delivery:new-order', (data) => {
            console.log('🔔 New order assigned:', data);
            fetchAllOrders();
        });

        socketRef.current.on('order:status-changed', (data) => {
            console.log('📦 Order status changed:', data);
            fetchAllOrders();
        });
    }, [user?._id, activeOrders, fetchAllOrders]);

    const disconnectSocket = useCallback(() => {
        if (socketRef.current) {
            console.log('🔌 Disconnecting delivery socket...');
            socketRef.current.disconnect();
            socketRef.current = null;
            setSocketConnected(false);
        }
    }, []);

    // ========== GEOLOCATION FUNCTIONS ==========
    const getLocationErrorMessage = (error) => {
        switch (error.code) {
            case error.PERMISSION_DENIED:
                return 'Location permission denied. Please enable location access in your browser settings.';
            case error.POSITION_UNAVAILABLE:
                return 'Location information unavailable. Please check your GPS/WiFi settings.';
            case error.TIMEOUT:
                return 'Location request timed out. Retrying with optimized settings...';
            default:
                return 'An unknown error occurred while getting location.';
        }
    };

    const startLocationTracking = () => {
        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by your browser');
            return;
        }

        setLocationLoading(true);
        setLocationError(null);

        const highAccuracyOptions = {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 10000
        };

        const lowAccuracyOptions = {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 30000
        };

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const location = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: position.timestamp
                };

                setCurrentLocation(location);
                setLocationLoading(false);
                setLocationEnabled(true);
                setLocationError(null);
                console.log('📍 High accuracy location acquired:', location);

                emitLocationUpdate(location);
                startWatchPosition(highAccuracyOptions);
            },
            (error) => {
                console.warn('⚠️ High accuracy failed, trying low accuracy...', error);

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const location = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            accuracy: position.coords.accuracy,
                            timestamp: position.timestamp
                        };

                        setCurrentLocation(location);
                        setLocationLoading(false);
                        setLocationEnabled(true);
                        setLocationError(null);
                        console.log('📍 Low accuracy location acquired:', location);

                        emitLocationUpdate(location);
                        startWatchPosition(lowAccuracyOptions);
                    },
                    (fallbackError) => {
                        console.error('❌ Both location strategies failed:', fallbackError);
                        setLocationError(getLocationErrorMessage(fallbackError));
                        setLocationLoading(false);
                        setLocationEnabled(false);
                    },
                    lowAccuracyOptions
                );
            },
            highAccuracyOptions
        );
    };

    const startWatchPosition = (options) => {
        const id = navigator.geolocation.watchPosition(
            (position) => {
                const location = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: position.timestamp
                };

                setCurrentLocation(location);
                setLocationError(null);
                console.log('📍 Location updated:', location);

                emitLocationUpdate(location);
            },
            (error) => {
                console.error('❌ Location watch error:', error);
                if (error.code === 3) {
                    console.warn('⚠️ Watch position timeout, will retry automatically...');
                } else {
                    setLocationError(getLocationErrorMessage(error));
                }
            },
            options
        );

        setWatchId(id);
        console.log('👀 Started watching position with ID:', id);
    };

    const stopLocationTracking = () => {
        if (watchId) {
            navigator.geolocation.clearWatch(watchId);
            setWatchId(null);
            console.log('🛑 Location tracking stopped');
        }
        setCurrentLocation(null);
        setLocationEnabled(false);
        setLocationError(null);
    };

    const retryLocation = () => {
        setLocationError(null);
        startLocationTracking();
    };

    useEffect(() => {
        return () => {
            if (watchId) {
                navigator.geolocation.clearWatch(watchId);
            }
        };
    }, [watchId]);

    // ========== CONNECT SOCKET WHEN ONLINE ==========
    useEffect(() => {
        if (isOnline && user?._id) {
            connectSocket();
        } else {
            disconnectSocket();
        }

        return () => {
            disconnectSocket();
        };
    }, [isOnline, user?._id, connectSocket, disconnectSocket]);

    // ========== REGISTER NEW ORDERS WITH SOCKET ==========
    useEffect(() => {
        if (socketConnected && activeOrders.length > 0) {
            activeOrders.forEach(order => {
                socketRef.current?.emit('delivery:register', {
                    orderId: order._id,
                    deliveryPersonId: user._id
                });
            });
        }
    }, [socketConnected, activeOrders, user?._id]);

    // ========== FETCH ORDERS ON MOUNT ==========
    useEffect(() => {
        fetchAllOrders();
    }, [fetchAllOrders]);

    useEffect(() => {
        let interval;
        if (user?._id) {
            interval = setInterval(() => {
                fetchAllOrders();
            }, 30000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [user?._id, fetchAllOrders]);

    // ========== HANDLE NAVIGATION FROM NOTIFICATION DROPDOWN ==========
    useEffect(() => {
        if (location.state) {
            const { selectedOrder, action, openOTPModal: shouldOpenOTP, openPaymentScanner: shouldOpenPayment } = location.state;
            
            console.log('📍 Navigated from notification with state:', location.state);
            
            if (selectedOrder && shouldOpenOTP && action === 'verifyOTP') {
                // Auto-open OTP verification modal
                console.log('🔐 Auto-opening OTP modal for order:', selectedOrder._id);
                setTimeout(() => {
                    openOTPVerificationModal(selectedOrder);
                }, 300);
            }
            
            if (selectedOrder && shouldOpenPayment && action === 'showQR') {
                // Auto-open payment scanner
                console.log('💰 Auto-opening payment scanner for order:', selectedOrder._id);
                setTimeout(() => {
                    openPaymentScanner(selectedOrder);
                }, 300);
            }
            
            // Clear navigation state after handling
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // ========== HANDLER FUNCTIONS ==========
    const handleAcceptOrder = async (orderId) => {
        try {
            const response = await axiosInstance.put(
                `/api/delivery-person/${user._id}/orders/${orderId}/accept-with-otp`
            );

            if (response.data) {
                setActiveOrders(prevOrders =>
                    prevOrders.map(order =>
                        order._id === orderId ? { ...order, status: 'shipped' } : order
                    )
                );

                if (socketRef.current?.connected) {
                    socketRef.current.emit('order:status-update', {
                        orderId,
                        status: 'shipped',
                        deliveryPersonId: user._id
                    });
                }

                setPopupData({
                    type: "success",
                    title: "Order Picked Up",
                    message: "Order picked up successfully! OTP sent to customer's email.",
                    onConfirm: () => {
                        setPopupData(null);
                        fetchAllOrders();
                    }
                });

                setTimeout(() => {
                    setPopupData(null);
                    fetchAllOrders();
                }, 2000);
            }
        } catch (error) {
            console.error('❌ Error accepting order:', error);

            setPopupData({
                type: "error",
                title: "Action Failed",
                message: "Failed to accept order. Please try again.",
                onConfirm: () => setPopupData(null)
            });

            setTimeout(() => setPopupData(null), 2500);
        }
    };

    const handleMarkDelivered = async (orderId) => {
        const order = activeOrders.find(o => o._id === orderId);
        if (!order) return;

        if (window.confirm(`Mark order #${order._id.slice(-6).toUpperCase()} as delivered?`)) {
            try {
                const response = await axiosInstance.put(`/api/orders/${orderId}`, {
                    status: 'delivered',
                    deliveryPersonId: user._id,
                    deliveredAt: new Date()
                });

                if (response.data) {
                    if (socketRef.current?.connected) {
                        socketRef.current.emit('order:status-update', {
                            orderId,
                            status: 'delivered',
                            deliveryPersonId: user._id
                        });
                    }
                    alert('✅ Order marked as delivered!');
                    fetchAllOrders();
                }
            } catch (error) {
                console.error('❌ Error marking order as delivered:', error);
                alert('❌ Failed to mark order as delivered. Please try again.');
            }
        }
    };

    const handleNavigate = (order) => {
        if (!currentLocation) {
            alert('⚠️ Location not available. Please enable location tracking from dashboard first.');
            return;
        }

        const deliveryAddress = order.address || 'Delivery Address';
        const origin = `${currentLocation.lat},${currentLocation.lng}`;
        const destinationEncoded = encodeURIComponent(deliveryAddress);
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destinationEncoded}&travelmode=driving`;

        window.open(mapsUrl, '_blank');
    };

    const handleViewOrderDetails = (order) => {
        setSelectedOrder(order);
        setShowOrderDetail(true);
    };

    // ========== UTILITY FUNCTIONS (CORRECTED - REMOVED 'processing') ==========
    const formatDate = (date) => {
        if (!date) return 'N/A';
        const d = new Date(date);
        return d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatOrderForDisplay = (order) => {
        return {
            id: `#ORD-${order._id?.slice(-6).toUpperCase()}`,
            _id: order._id,
            customer: order.name || 'Customer',
            phone: order.phoneNumber || 'N/A',
            delivery: order.address || 'Delivery Address',
            amount: parseFloat(order.amount) || 0,
            status: order.status,
            items: order.products?.length || 0
        };
    };

    // CORRECTED: Removed 'processing' from statusMap
    const getStatusDisplay = (status) => {
        const statusMap = {
            'confirmed': { emoji: '🔔', text: 'New Order' },
            'shipped': { emoji: '🚚', text: 'Picked Up' },
            'out_for_delivery': { emoji: '🚀', text: 'Out for Delivery' },
            'delivered': { emoji: '✅', text: 'Delivered' },
            'cancelled': { emoji: '❌', text: 'Cancelled' }
        };
        return statusMap[status] || { emoji: '📦', text: status };
    };

    if (!user) {
        return (
            <div className="delivery-history">
                <div className="loading-container">
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    const currentOrders =
        activeTab === 'myOrders'
            ? activeOrders
            : activeTab === 'delivered'
                ? deliveredOrders
                : cancelledOrders;


    return (
        <div className="delivery-history">
            {/* Header */}
            <div className="history-header">
                <div className="header-left">
                    <button className="history-back-btn" onClick={() => navigate('/dashboard')}>
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1>Order History</h1>
                        <p>View your active and delivered orders</p>
                    </div>
                </div>
                <div className="header-right">
                    <div className={`status-badge ${isOnline ? 'online' : 'offline'}`}>
                        <span className="status-dot"></span>
                        {isOnline ? 'Online' : 'Offline'}
                    </div>

                    <div
                        className={`location-toggle-display ${locationEnabled ? 'active' : 'inactive'}`}
                        title="GPS is controlled from dashboard"
                    >
                        {locationLoading ? (
                            <Loader size={20} className="spin-icon" />
                        ) : locationEnabled ? (
                            <MapPin size={20} />
                        ) : (
                            <MapPinOff size={20} />
                        )}
                        <span className="location-toggle-text">
                            {locationLoading ? 'Loading...' : (locationEnabled ? 'GPS On' : 'GPS Off')}
                        </span>
                    </div>

                    {socketConnected && (
                        <div className="socket-indicator">
                            <span className="pulse-dot"></span>
                        </div>
                    )}
                </div>
            </div>

            {locationError && (
                <div className="location-error-bar">
                    <AlertCircle size={16} />
                    <span>{locationError}</span>
                    <button className="retry-btn" onClick={retryLocation}>
                        Retry
                    </button>
                </div>
            )}

            {/* OTP VERIFICATION MODAL */}
            {showOTPModal && selectedOrderForOTP && (
                <div className="modal-overlay" onClick={() => setShowOTPModal(false)}>
                    <div className="modal-content otp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>🔐 Verify Delivery OTP</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowOTPModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="otp-info-section">
                                <p className="otp-instruction">
                                    An OTP has been sent to the customer's email.
                                    Please ask the customer for the 6-digit OTP to proceed with delivery.
                                </p>

                                <div className="order-brief">
                                    <p><strong>Order ID:</strong> #{selectedOrderForOTP._id.slice(-6).toUpperCase()}</p>
                                    <p><strong>Customer:</strong> {selectedOrderForOTP.name}</p>
                                    <p><strong>Amount:</strong> ₹{parseFloat(selectedOrderForOTP.amount).toFixed(2)}</p>
                                </div>
                            </div>

                            <div className="otp-input-section">
                                <label htmlFor="delivery-otp">Enter 6-Digit OTP</label>
                                <input
                                    id="delivery-otp"
                                    type="text"
                                    maxLength="6"
                                    placeholder="000000"
                                    value={deliveryOTP}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        setDeliveryOTP(value);
                                        setOtpError('');
                                    }}
                                    className="otp-input"
                                    disabled={otpLoading}
                                />

                                {otpError && (
                                    <p className="otp-error">
                                        ⚠️ {otpError}
                                        {otpAttemptsLeft > 0 && otpAttemptsLeft < 5 && (
                                            <span> ({otpAttemptsLeft} attempts left)</span>
                                        )}
                                    </p>
                                )}
                            </div>

                            <div className="otp-validity-info">
                                <p>⏰ OTP is valid for 12 hours from pickup time</p>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn-modal-primary"
                                onClick={handleVerifyDeliveryOTP}
                                disabled={otpLoading || deliveryOTP.length !== 6}
                            >
                                {otpLoading ? (
                                    <>
                                        <Loader size={16} className="spin-icon" />
                                        Verifying...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle size={16} />
                                        Verify
                                    </>
                                )}
                            </button>

                            <button
                                className="btn-secondary"
                                onClick={handleResendDeliveryOTP}
                                disabled={otpLoading}
                            >
                                Resend OTP
                            </button>

                            <button
                                className="btn-secondary"
                                onClick={() => setShowOTPModal(false)}
                                disabled={otpLoading}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* UPI PAYMENT SCANNER */}
            {showPaymentScanner && selectedOrderForPayment && (
                <div className="fixed-payment-scanner">
                    <div className="payment-scanner-body">
                        <div className="order-info-banner">
                            <div className="payment-scanner-header">
                                <button className="close-scanner-btn" onClick={closePaymentScanner}>
                                    <XCircle size={24} />
                                </button>
                            </div>

                            <div>
                                <p className="order-id-text">Order: #{selectedOrderForPayment._id.slice(-6).toUpperCase()}</p>
                                <p className="customer-name-text">Customer: {selectedOrderForPayment.name}</p>
                            </div>
                            <div className="amount-display">
                                <span className="amount-label">Amount</span>
                                <span className="amount-value">₹{parseFloat(selectedOrderForPayment.amount).toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="qr-code-container">
                            <div className="qr-code-wrapper">
                                <QRCode
                                    value={generateUPIString(selectedOrderForPayment)}
                                    size={220}
                                    level="H"
                                    style={{ padding: '16px', background: 'white', borderRadius: '12px' }}
                                />
                            </div>
                            <p className="scan-instruction">📱 Customer can scan with any UPI app</p>
                        </div>

                        <div className="upi-details-box">
                            <p className="upi-header">Prefer online payment? Scan to pay ₹{parseFloat(selectedOrderForPayment.amount).toFixed(2)}!</p>
                            <div className="upi-info">
                                <p><strong>UPI ID:</strong> rismicmartpriva145433.rzp@rxairtel</p>
                                <p><strong>Name (payee):</strong> Rismicmart Private Limited</p>
                            </div>
                            <p className="upi-note">Use any UPI app to pay securely & instantly.</p>
                        </div>

                        <div className="payment-actions">
                            <button className="btn-payment-received" onClick={confirmPaymentReceived}>
                                <CheckCircle size={20} />
                                Payment Received - Mark Delivered
                            </button>
                            <button className="btn-collect-cash" onClick={closePaymentScanner}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab Navigation */}
            <div className="tabs-container">
                <button
                    className={`tab-btn ${activeTab === 'myOrders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('myOrders')}
                >
                    <Package size={18} />
                    My Orders ({activeOrders.length})
                </button>

                <button
                    className={`tab-btn ${activeTab === 'delivered' ? 'active' : ''}`}
                    onClick={() => setActiveTab('delivered')}
                >
                    <CheckCircle size={18} />
                    Delivered ({deliveredOrders.length})
                </button>

                <button
                    className={`tab-btn cancelled ${activeTab === 'cancelled' ? 'active' : ''}`}
                    onClick={() => setActiveTab('cancelled')}
                >
                    <XCircle size={18} />
                    Cancelled ({cancelledOrders.length})
                </button>
            </div>

            {/* Orders Section */}
            <div className="content-section active-orders-section">
                <div className="section-header">
                    <h3>
                        {activeTab === 'myOrders'
                            ? 'Active Orders'
                            : activeTab === 'delivered'
                                ? 'Delivered Orders'
                                : 'Cancelled Orders'}
                    </h3>
                    <span className="order-count">{currentOrders.length} Orders</span>
                </div>

                {activeTab === 'myOrders' && !isOnline && (
                    <div className="offline-message">
                        <AlertCircle size={48} />
                        <h3>You're currently offline</h3>
                        <p>Go to dashboard and turn on your status to receive orders</p>
                        <button className="btn-primary" onClick={() => navigate('/dashboard')}>
                            Go to Dashboard
                        </button>
                    </div>
                )}

                {fetchingOrders && (
                    <div className="loading-message" style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner"></div>
                        <p>Loading orders...</p>
                    </div>
                )}

                {!fetchingOrders && currentOrders.length === 0 && (
                    <div className="empty-state">
                        <Package size={64} />
                        <h3>No {activeTab === 'myOrders' ? 'active' : activeTab === 'delivered' ? 'delivered' : 'cancelled'} orders</h3>
                        <p>
                            {activeTab === 'myOrders'
                                ? 'New orders will appear here when assigned to you'
                                : activeTab === 'delivered'
                                    ? 'Completed deliveries will be shown here'
                                    : 'Cancelled orders will be displayed here'}
                        </p>
                    </div>
                )}

                {!fetchingOrders && currentOrders.length > 0 && (
                    <div className="orders-list">
                        {currentOrders.map((orderData) => {
                            const order = formatOrderForDisplay(orderData);
                            const statusDisplay = getStatusDisplay(orderData.status);

                            return (
                                <div key={order._id} className="order-card">
                                    <div className="order-header">
                                        <div className="order-id-section">
                                            <span className="order-id">{order.id}</span>
                                            <span className={`order-status ${orderData.status}`}>
                                                {statusDisplay.emoji} {statusDisplay.text}
                                            </span>
                                        </div>
                                        <div className="order-amount">₹{order.amount.toFixed(2)}</div>
                                    </div>

                                    <div className="order-customer">
                                        <div className="customer-info">
                                            <h4>{order.customer}</h4>
                                            <p className="order-items">{order.items} items</p>
                                        </div>
                                        <a href={`tel:${order.phone}`} className="phone-btn">
                                            <Phone size={18} />
                                        </a>
                                    </div>

                                    <div className="order-delivery-address">
                                        <MapPin size={14} />
                                        <span>{order.delivery}</span>
                                    </div>

                                    {/* COD Badge */}
                                    {orderData.paymentMethod === 'Cash on Delivery' && activeTab === 'myOrders' && (
                                        <div className="cod-badge">
                                            💵 Cash on Delivery - ₹{order.amount.toFixed(2)}
                                        </div>
                                    )}

                                    {/* Cancellation Info */}
                                    {activeTab === 'cancelled' && orderData.cancellationReason && (
                                        <div className="cancellation-info">
                                            <p><strong>Reason:</strong> {orderData.cancellationReason}</p>
                                            {orderData.cancelledBy && (
                                                <p><strong>Cancelled by:</strong> {orderData.cancelledBy}</p>
                                            )}
                                            {orderData.cancelledAt && (
                                                <p><strong>Cancelled at:</strong> {formatDate(orderData.cancelledAt)}</p>
                                            )}
                                        </div>
                                    )}

                                    <div className="order-footer">
                                        <div className="order-meta">
                                            <span className="meta-item">
                                                <Package size={14} /> {order.items} items
                                            </span>
                                            {activeTab === 'myOrders' && locationEnabled && currentLocation && (
                                                <span className="meta-item location-indicator">
                                                    <MapPin size={12} className="pulse-icon" />
                                                    Live Tracking
                                                </span>
                                            )}
                                            {activeTab === 'delivered' && orderData.deliveredAt && (
                                                <span className="meta-item">
                                                    <CheckCircle size={14} /> {formatDate(orderData.deliveredAt)}
                                                </span>
                                            )}
                                        </div>

                                        <div className="order-actions">
                                            <button
                                                className="btn-view-details"
                                                onClick={() => handleViewOrderDetails(orderData)}
                                            >
                                                <Eye size={16} />
                                                View
                                            </button>

                                            {activeTab === 'myOrders' && (
                                                <>
                                                    <div className="action-row">
                                                        {orderData.status === 'confirmed' ? (
                                                            <button
                                                                className="btn-primary"
                                                                onClick={() => handleAcceptOrder(order._id)}
                                                            >
                                                                <CheckCircle size={16} />
                                                                Pick Up
                                                            </button>
                                                        ) : orderData.status === 'shipped' ? (
                                                            <button
                                                                className="btn-primary"
                                                                onClick={() => openOTPVerificationModal(orderData)}
                                                            >
                                                                <CheckCircle size={16} />
                                                                Verify OTP
                                                            </button>
                                                        ) : orderData.status === 'out_for_delivery' && orderData.paymentMethod === 'Cash on Delivery' ? (
                                                            <button
                                                                className="btn-upi-payment"
                                                                onClick={() => openPaymentScanner(orderData)}
                                                            >
                                                                <QrCode size={16} />
                                                                Payment QR
                                                            </button>
                                                        ) : orderData.status === 'out_for_delivery' ? (
                                                            <button
                                                                className="btn-primary"
                                                                onClick={() => handleMarkDelivered(order._id)}
                                                            >
                                                                <CheckCircle size={16} />
                                                                Mark Delivered
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                    <button
                                                        className="btn-view-details"
                                                        onClick={() => handleNavigate(orderData)}
                                                        disabled={!currentLocation}
                                                        title={!currentLocation ? 'Enable GPS from dashboard first' : 'Navigate to customer'}
                                                    >
                                                        <Navigation size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Order Detail Modal */}
            {showOrderDetail && selectedOrder && (
                <div className="modal-overlay" onClick={() => setShowOrderDetail(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Order Details</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowOrderDetail(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="order-detail-section">
                                <div className="detail-row">
                                    <span className="label">Order ID:</span>
                                    <span className="value">#{selectedOrder._id.slice(-6).toUpperCase()}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Status:</span>
                                    <span className={`order-status ${selectedOrder.status}`}>
                                        {getStatusDisplay(selectedOrder.status).emoji} {getStatusDisplay(selectedOrder.status).text}
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Order Date:</span>
                                    <span className="value">{formatDate(selectedOrder.date)}</span>
                                </div>
                                {selectedOrder.deliveredAt && (
                                    <div className="detail-row">
                                        <span className="label">Delivered At:</span>
                                        <span className="value">{formatDate(selectedOrder.deliveredAt)}</span>
                                    </div>
                                )}
                                {selectedOrder.cancelledAt && (
                                    <div className="detail-row">
                                        <span className="label">Cancelled At:</span>
                                        <span className="value">{formatDate(selectedOrder.cancelledAt)}</span>
                                    </div>
                                )}
                            </div>

                            <div className="order-detail-section">
                                <h3>Customer Information</h3>
                                <div className="detail-row">
                                    <span className="label">Name:</span>
                                    <span className="value">{selectedOrder.name}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Phone:</span>
                                    <span className="value">
                                        <a href={`tel:${selectedOrder.phoneNumber}`}>
                                            <Phone size={14} /> {selectedOrder.phoneNumber}
                                        </a>
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Email:</span>
                                    <span className="value">{selectedOrder.email || 'N/A'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Address:</span>
                                    <span className="value">{selectedOrder.address}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Pincode:</span>
                                    <span className="value">{selectedOrder.pincode}</span>
                                </div>
                            </div>

                            {currentLocation && selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && (
                                <div className="order-detail-section">
                                    <h3>Your Current Location</h3>
                                    <div className="detail-row">
                                        <MapPin size={16} className="pulse-icon" />
                                        <span className="value">
                                            Lat: {currentLocation.lat.toFixed(6)},
                                            Lng: {currentLocation.lng.toFixed(6)}
                                            ({currentLocation.accuracy.toFixed(0)}m accuracy)
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="order-detail-section">
                                <h3>Order Items ({selectedOrder.products?.length || 0})</h3>
                                <div className="items-list">
                                    {selectedOrder.products?.map((product, index) => (
                                        <div key={index} className="item-row">
                                            <div className="item-info">
                                                {product.image && (
                                                    <img
                                                        src={product.image}
                                                        alt={product.productTitle}
                                                        className="item-image"
                                                    />
                                                )}
                                                <div>
                                                    <strong>{product.productTitle}</strong>
                                                    {product.selectedVariantOption && (
                                                        <span className="variant">
                                                            Variant: {product.selectedVariantOption}
                                                        </span>
                                                    )}
                                                    <span className="quantity">Qty: {product.quantity}</span>
                                                </div>
                                            </div>
                                            <div className="item-price">
                                                <span className="unit-price">₹{product.price} × {product.quantity}</span>
                                                <strong>₹{product.subTotal}</strong>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="order-detail-section">
                                <h3>Payment Information</h3>
                                <div className="detail-row">
                                    <span className="label">Payment Method:</span>
                                    <span className="value">
                                        {selectedOrder.paymentMethod}
                                        {selectedOrder.paymentMethod === 'Cash on Delivery' && ' 💵'}
                                    </span>
                                </div>
                                {selectedOrder.paymentId && (
                                    <div className="detail-row">
                                        <span className="label">Payment ID:</span>
                                        <span className="value">{selectedOrder.paymentId}</span>
                                    </div>
                                )}
                                {selectedOrder.upiVerified && (
                                    <div className="detail-row">
                                        <span className="label">UPI Verified:</span>
                                        <span className="value verified-badge">
                                            <CheckCircle size={14} /> Verified at {formatDate(selectedOrder.upiVerifiedAt)}
                                        </span>
                                    </div>
                                )}
                                <div className="detail-row total-row">
                                    <span className="label">Total Amount:</span>
                                    <strong className="value total-amount">
                                        ₹{parseFloat(selectedOrder.amount).toFixed(2)}
                                    </strong>
                                </div>
                            </div>

                            {selectedOrder.status === 'cancelled' && selectedOrder.cancellationReason && (
                                <div className="order-detail-section">
                                    <h3>Cancellation Details</h3>
                                    <div className="detail-row">
                                        <span className="label">Reason:</span>
                                        <span className="value">{selectedOrder.cancellationReason}</span>
                                    </div>
                                    {selectedOrder.cancelledBy && (
                                        <div className="detail-row">
                                            <span className="label">Cancelled By:</span>
                                            <span className="value">{selectedOrder.cancelledBy}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            {selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && (
                                <>
                                    <button
                                        className="btn-navigate"
                                        onClick={() => {
                                            setShowOrderDetail(false);
                                            handleNavigate(selectedOrder);
                                        }}
                                        disabled={!currentLocation}
                                    >
                                        <Navigation size={16} />
                                        Navigate
                                    </button>
                                    {selectedOrder.status === 'confirmed' ? (
                                        <button
                                            className="btn-modal-primary"
                                            onClick={() => {
                                                setShowOrderDetail(false);
                                                handleAcceptOrder(selectedOrder._id);
                                            }}
                                        >
                                            <CheckCircle size={16} />
                                         Pick Up
                                        </button>
                                    ) : selectedOrder.status === 'shipped' ? (
                                        <button
                                            className="btn-modal-primary"
                                            onClick={() => {
                                                setShowOrderDetail(false);
                                                openOTPVerificationModal(selectedOrder);
                                            }}
                                        >
                                            <CheckCircle size={16} />
                                            Verify OTP
                                        </button>
                                    ) : selectedOrder.status === 'out_for_delivery' && selectedOrder.paymentMethod === 'Cash on Delivery' ? (
                                        <button
                                            className="btn-modal-primary"
                                            onClick={() => {
                                                setShowOrderDetail(false);
                                                openPaymentScanner(selectedOrder);
                                            }}
                                        >
                                            <QrCode size={16} />
                                            Payment QR
                                        </button>
                                    ) : selectedOrder.status === 'out_for_delivery' ? (
                                        <button
                                            className="btn-modal-primary"
                                            onClick={() => {
                                                setShowOrderDetail(false);
                                                handleMarkDelivered(selectedOrder._id);
                                            }}
                                        >
                                            <CheckCircle size={16} />
                                            Delivered
                                        </button>
                                    ) : null}
                                </>
                            )}
                            <button
                                className="btn-secondary"
                                onClick={() => setShowOrderDetail(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Universal Popup */}
            {popupData && (
                <div className="popup-overlay" onClick={() => setPopupData(null)}>
                    <div
                        className={`popup-box ${popupData.type}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="popup-header">
                            <span className="popup-icon">
                                {popupData.type === "success"
                                    ? "✅"
                                    : popupData.type === "error"
                                        ? "❌"
                                        : "⚠️"}
                            </span>
                            <h3>{popupData.title}</h3>
                        </div>

                        <p className="popup-message">{popupData.message}</p>

                        <div className="popup-actions">
                            {popupData.type === "confirm" ? (
                                <>
                                    <button
                                        className="btn-confirm"
                                        onClick={() => {
                                            if (typeof popupData.onConfirm === "function")
                                                popupData.onConfirm();
                                        }}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        className="btn-cancel"
                                        onClick={() => setPopupData(null)}
                                    >
                                        No
                                    </button>
                                </>
                            ) : (
                                <button
                                    className="btn-ok"
                                    onClick={() => {
                                        if (typeof popupData.onConfirm === "function")
                                            popupData.onConfirm();
                                        else setPopupData(null);
                                    }}
                                >
                                    OK
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeliveryHistory;

